import {
  UPCOMING_GAMES_LABEL,
  TODAYS_GAMES_LABEL,
  LIVE_EVENTS_LABEL,
} from 'constants/names.js';
import { DEFAULT_EVENTS_FEED } from 'constants/localStorageKey.js';
import useLocalStorage from 'hooks/useLocalStorage';
// TEMP - Don't show login until later...
import { ROUTES } from 'constants';
//import { ROUTES, SITES_WITH_AUTH } from 'constants';
import React, { useEffect, useMemo, useState } from 'react';
import { createSelector } from 'reselect';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'utils/toast';
import { LOCATION_MESSAGES } from 'locales/en-US';
import { onMessageListener, fetchToken, safeLogEvent } from 'firebase.js';
import { isEmptyObject } from 'helpers';
import { getAllTeamsAction } from 'redux/teams/actionCreators.js';
import {
  getAllGamesAction,
  getUpcomingGamesAction,
  allGamesResetAction,
} from 'redux/games/actionCreators.js';
import {
  getTeamAppAction,
  clearTeamAppAction,
} from 'redux/teamApp/actionCreators.js';
import types from 'redux/types';

// TEMP: Don't show news feed
// TODO: Update news more regularly and determine if should use espn news or custom news, etc

import {
  Header,
  ChoiceTitle,
  AppStoreLinks,
  ErrorMessage,
  NewsFeed,
  NotificationComponent,
  Loading,
  TopNav,
  Ads,
} from 'components';
import OnboardingOverlay from 'components/OnboardingOverlay';
import PushNotificationPermissionModal from 'components/PushNotificationPermissionModal';
import LocationPermissionModal from 'components/LocationPermissionModal';
import useOnboarding from 'hooks/useOnboarding';
import usePushNotificationPermission from 'hooks/usePushNotificationPermission';
import useLocationPermission from 'hooks/useLocationPermission';
import { useTeamColors } from 'hooks';
import SEO from 'components/SEO/SEO';
import Dashboard from 'components/Dashboard';
import DashboardOneTeam from 'components/Dashboard/DashboardOneTeam';
import { detectInAppWebView } from 'utils/browserDetection';

import 'react-toastify/dist/ReactToastify.css';
import {
  BodyContainer,
  MainContainer,
  StyledHeader,
  StyledFooter,
} from 'components/App/style';

// Feature flags will be checked in component

const memoizedGamesState = createSelector(
  [(state) => state?.games],
  (games) => ({
    games: games.data,
    loading: games.loading,
    error: games.error,
    current: games.current,
  })
);

const memoizedConfigState = createSelector(
  [(state) => state.configuration],
  (configuration) => ({
    data: configuration.data,
    loading: configuration.loading,
    error: configuration.error,
  })
);

const MainPage = ({ setEventLeague }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const params = Object.fromEntries([...searchParams]);
  const { mainColorRgba } = useTeamColors();

  const isTopNavEnabled = useSelector((state) => {
    const val = state.configuration?.data?.top_nav_enabled;
    return val === 1 || val === '1';
  });

  // Feature flags - handle both string '1' and integer 1 from API
  const isNewsEnabled = useSelector((state) => {
    const val = state.configuration?.data?.news_enabled;
    return val === 1 || val === '1';
  });
  const isLocationServicesEnabled = useSelector((state) => {
    const val = state.configuration?.data?.location_services_enabled;
    return val === 1 || val === '1';
  });
  const isOnboardingEnabled = useSelector((state) => {
    const val = state.configuration?.data?.onboarding_enabled;
    return val === 1 || val === '1';
  });
  const isPushNotificationsEnabled = useSelector((state) => {
    const val = state.configuration?.data?.push_notifications_enabled;
    return val === 1 || val === '1';
  });

  // Get user data for push notifications
  const userData = useSelector((state) => state.user?.data);
  const userId = userData?.user_id;
  const hostName = window.location.hostname;

  // Onboarding, push notification, and location permission state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mountOnboarding, setMountOnboarding] = useState(false);
  const [showPushPermissionModal, setShowPushPermissionModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const { hasCompletedOnboarding, completeOnboarding } = useOnboarding();
  const {
    hasRequestedPermission,
    hasDismissedPermission,
    markAsRequested: markPushAsRequested,
    markAsMaybeLater: markPushAsMaybeLater,
  } = usePushNotificationPermission();
  const {
    shouldPrompt,
    incrementPromptCount,
    setPermissionGranted,
    setPermissionDenied,
    setMaybeLater,
    isThirdPrompt,
  } = useLocationPermission();

  const {
    games,
    loading: isGamesLoading,
    current: currentGame,
    error: gamesError,
  } = useSelector(memoizedGamesState);

  const {
    loading: isConfigLoading,
    error: isConfigError,
    data: configData,
  } = useSelector(memoizedConfigState);

  const todaysGames = useSelector((state) => state.todaysGames.data);
  const liveGames = useSelector((state) => state.liveGames.data);
  const favoriteTeams = useSelector((state) => state.favoriteTeams.data);
  const [defaultEventsFeed] = useLocalStorage(
    DEFAULT_EVENTS_FEED,
    LIVE_EVENTS_LABEL
  );
  const isLiveEventsFeed = defaultEventsFeed !== TODAYS_GAMES_LABEL;
  // Fall back to Today's Events whenever Live Events has nothing to show
  // (no games currently in progress), rather than rendering an empty feed.
  const eventsFeedGames =
    isLiveEventsFeed && liveGames?.length ? liveGames : todaysGames;

  // Live Events is fetched once on initial app load (alongside the
  // leagues list) but scores/status go stale within minutes - refetch
  // whenever the user (re)selects Live Events, since switching feeds alone
  // doesn't otherwise trigger a new request.
  useEffect(() => {
    if (isLiveEventsFeed) {
      dispatch({ type: types.GET_LIVE_GAMES_START });
    }
  }, [isLiveEventsFeed, dispatch]);

  // Set up Firebase Cloud Messaging listener (only if supported)
  useEffect(() => {
    // Only set up message listener if Firebase messaging is supported
    try {
      onMessageListener()
        .then((payload) => {
          if (payload?.notification) {
            toast(
              <NotificationComponent
                body={payload.notification.body}
                title={payload.notification.title}
              />,
              { autoClose: 8000 }
            );
          }
        })
        .catch((err) => {
          // Silently fail if messaging not supported - this is expected in some browsers
          console.debug('Firebase messaging not available:', err.message);
        });
    } catch (err) {
      // Firebase messaging not supported in this browser
      console.debug('Firebase messaging initialization skipped:', err.message);
    }
  }, []); // Run once on mount

  useEffect(() => {
    if (
      configData &&
      (configData?.display_custom_events === '1' ||
        configData?.display_custom_events === 1)
    ) {
      navigate(`/${ROUTES.CUSTOM_EVENTS}`);
    }
  }, [configData, navigate]);

  useEffect(() => {
    const off = (v) => v === '0' || v === 0;
    const on = (v) => v === '1' || v === 1;
    // Only fires for sites where display_custom_events is also off - a
    // site with all three display_* flags off AND custom events on (e.g.
    // tickti.me) is a genuine custom-events site, not a degenerate/
    // championships-redirect one; without this guard both effects fired
    // and raced, and this one crashed on a null one_league_name since
    // custom-events sites have no league at all - confirmed via direct API
    // response inspection for tickti.me.
    if (
      off(configData?.display_leagues_select) &&
      off(configData?.display_one_team) &&
      off(configData?.display_teams_select) &&
      !on(configData?.display_custom_events) &&
      configData?.one_league_name
    ) {
      navigate(
        `/${
          ROUTES.CHAMPIONSHIPS
        }?league=${configData.one_league_name.toLowerCase()}&leagueId=${
          configData?.one_league_id
        }`
      );
    }
  }, [configData, navigate]);

  useEffect(() => {
    if (
      params &&
      params?.leagueId &&
      configData?.display_teams_select !== '1'
    ) {
      dispatch(allGamesResetAction());
      dispatch(getAllTeamsAction(params?.leagueId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.leagueId]);

  useEffect(() => {
    if (
      params &&
      params?.teamId &&
      configData?.display_one_team !== '1' &&
      !params?.collegeLeagueId &&
      params.team !== UPCOMING_GAMES_LABEL
    ) {
      dispatch(getAllGamesAction(params?.teamId, params?.leagueId));
      // Fetch team app data when a team is selected
      dispatch(getTeamAppAction(params?.teamId));
    } else if (
      configData?.display_teams_select !== '1' &&
      params.team === UPCOMING_GAMES_LABEL
    ) {
      // Clear team app data when showing upcoming games
      dispatch(clearTeamAppAction());
      dispatch(getUpcomingGamesAction(params?.leagueId));
    }
    // eslint-disable-next-line
  }, [params?.teamId]);

  useEffect(() => {
    const isOneTeam =
      configData?.display_one_team === '1' ||
      configData?.display_one_team === 1;
    if (isOneTeam && configData.sport_level !== 'Collegiate') {
      dispatch(
        getAllGamesAction(configData?.one_team_id, configData?.one_league_id)
      );
      // Fetch team app data for single-team sites
      dispatch(getTeamAppAction(configData?.one_team_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData?.display_one_team, configData?.one_team_id]);

  useEffect(() => {
    if (
      configData?.display_teams_select === '1' ||
      configData?.display_teams_select === 1
    ) {
      // A league in the URL wins over the site's configured league, so a
      // multi-league site (Playoffs Time: NBA + MLB) lists the right teams
      // when linked to a league (e.g. ?league=MLB&leagueId=3). Normal visits
      // are unchanged: Wrapper fills the URL with one_league_id itself.
      const leagueId = params?.leagueId || configData.one_league_id;
      dispatch(getAllTeamsAction(leagueId));

      // Only load league-wide upcoming games if NO team is selected -
      // if a team is selected, the team's schedule is loaded by the
      // useEffect above instead.
      if (!params?.teamId) {
        dispatch(getUpcomingGamesAction(leagueId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configData?.display_teams_select,
    configData?.one_league_id,
    params?.leagueId,
  ]);

  useEffect(() => {
    if (location.state?.fromDirectLink) {
      const { league, leagueId, team, teamId, eventId } = location.state;
      setEventLeague(league);
      dispatch(allGamesResetAction());
      dispatch(getAllTeamsAction(leagueId));

      // Clear the location state to prevent re-triggering
      navigate('/', { replace: true });

      // Set URL parameters for the event
      const searchParams = new URLSearchParams({
        league,
        leagueId,
        team,
        teamId,
        eventId,
      });
      navigate(`/?${searchParams.toString()}`, { replace: true });
    }
  }, [location.state, navigate, dispatch, setEventLeague]);

  // Show onboarding after 5 second delay - only if enabled
  const isOnboardingCompleted = hasCompletedOnboarding();
  useEffect(() => {
    if (isOnboardingEnabled && !isOnboardingCompleted) {
      const timer = setTimeout(() => {
        // Re-check fresh at fire time, not the value closed over when the timer was armed
        if (hasCompletedOnboarding()) return;
        // Mount component first
        setMountOnboarding(true);
        // Then show it after a small delay to trigger CSS transition
        setTimeout(() => {
          setShowOnboarding(true);
          // Track when onboarding overlay is viewed
          safeLogEvent('onboarding_viewed', {
            timestamp: new Date().toISOString(),
          });
        }, 50);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOnboardingEnabled, isOnboardingCompleted, hasCompletedOnboarding]);

  // Listen for user login event to show push notification modal
  useEffect(() => {
    const handleUserLogin = () => {
      if (
        isPushNotificationsEnabled &&
        !hasRequestedPermission() &&
        !hasDismissedPermission()
      ) {
        setTimeout(() => {
          setShowPushPermissionModal(true);

          // Track prompt shown with current dismiss count
          const currentState = JSON.parse(
            localStorage.getItem('pushNotificationPermission') || '{}'
          );
          const dismissCount = currentState.dismissCount || 0;

          safeLogEvent('push_permission_prompt_shown', {
            context: 'post_login',
            dismiss_count: dismissCount,
            is_retry: dismissCount > 0,
            timestamp: new Date().toISOString(),
          });
        }, 500);
      }
    };

    window.addEventListener('userLoggedIn', handleUserLogin);
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, [
    isPushNotificationsEnabled,
    hasRequestedPermission,
    hasDismissedPermission,
  ]);

  // Check for push notification retry when user returns to page or navigates
  useEffect(() => {
    if (!isPushNotificationsEnabled || !userId) return;

    const checkPushNotificationRetry = () => {
      const shouldShow = !hasRequestedPermission() && !hasDismissedPermission();

      if (shouldShow && !showPushPermissionModal) {
        setShowPushPermissionModal(true);

        // Track retry prompt with dismiss count
        const currentState = JSON.parse(
          localStorage.getItem('pushNotificationPermission') || '{}'
        );
        const dismissCount = currentState.dismissCount || 0;

        safeLogEvent('push_permission_prompt_shown', {
          context: 'retry_after_delay',
          dismiss_count: dismissCount,
          is_retry: true,
          timestamp: new Date().toISOString(),
        });
      }
    };

    // Check when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPushNotificationRetry();
      }
    };

    // Check when window gains focus (user switches back to window)
    const handleFocus = () => {
      checkPushNotificationRetry();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    isPushNotificationsEnabled,
    userId,
    hasRequestedPermission,
    hasDismissedPermission,
    showPushPermissionModal,
  ]);

  const shouldDisplayMainDashboard = useMemo(
    () =>
      (configData?.display_leagues_select === '1' ||
        configData?.display_leagues_select === 1) &&
      (configData?.display_one_team === '0' ||
        configData?.display_one_team === 0) &&
      (configData?.display_teams_select === '0' ||
        configData?.display_teams_select === 0) &&
      (configData?.display_custom_events === '0' ||
        configData?.display_custom_events === 0),
    [
      configData?.display_custom_events,
      configData?.display_leagues_select,
      configData?.display_one_team,
      configData?.display_teams_select,
    ]
  );

  const shouldDisplayOneLeagueMainDashboard = useMemo(
    () =>
      (configData?.display_teams_select === '1' ||
        configData?.display_teams_select === 1) &&
      (configData?.display_leagues_select === '0' ||
        configData?.display_leagues_select === 0) &&
      (configData?.display_one_team === '0' ||
        configData?.display_one_team === 0),
    [
      configData?.display_leagues_select,
      configData?.display_one_team,
      configData?.display_teams_select,
    ]
  );

  if (gamesError) {
    safeLogEvent('select_content', {
      content_type: 'error',
      content_id: `Unable to show games schedule  for ${window.location.href}`,
    });
  }

  if (isConfigLoading) return <Loading />;

  if (!isConfigLoading && !!isConfigError) {
    safeLogEvent('select_content', {
      content_type: 'error',
      content_id: `Unable to load site configuration for ${window.location.href}`,
    });

    return <ErrorMessage />;
  }

  const isNotSelectionMade =
    (configData.display_leagues_select === '1' ||
      configData.display_leagues_select === 1) &&
    isEmptyObject(params);

  // Onboarding handlers
  const handleOnboardingComplete = () => {
    completeOnboarding();
    setShowOnboarding(false);
    setMountOnboarding(false);

    // Show location permission modal after onboarding - only if location services enabled
    if (isLocationServicesEnabled && shouldPrompt()) {
      setTimeout(() => {
        setShowLocationModal(true);
        safeLogEvent('location_permission_prompt', {
          context: 'post_onboarding',
        });
      }, 500);
    }
  };

  // Push notification permission handlers
  const handlePushPermissionAllow = () => {
    setShowPushPermissionModal(false);
    markPushAsRequested();

    // Get dismiss count before marking as requested
    const currentState = JSON.parse(
      localStorage.getItem('pushNotificationPermission') || '{}'
    );
    const dismissCount = currentState.dismissCount || 0;

    // Request push notification permission and get token
    if (userId) {
      fetchToken({ userId, siteUrl: hostName });

      // Dispatch event to update push notification status in UserProfile
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('pushPermissionGranted'));
      }, 2000);
    }

    // Track final decision to allow with full journey context
    safeLogEvent('push_permission_granted', {
      context: 'permission_modal',
      user_id: userId,
      dismiss_count: dismissCount,
      decided_after_dismissals: dismissCount,
      timestamp: new Date().toISOString(),
    });

    // Location modal operates independently - removed from here
  };

  const handlePushPermissionMaybeLater = () => {
    setShowPushPermissionModal(false);

    // Get state before updating to track current count
    const currentState = JSON.parse(
      localStorage.getItem('pushNotificationPermission') || '{}'
    );
    const previousDismissCount = currentState.dismissCount || 0;
    const newDismissCount = previousDismissCount + 1;

    markPushAsMaybeLater();

    // Check if test mode is enabled for delay calculation
    const isTestMode =
      process.env.REACT_APP_PUSH_NOTIFICATION_TEST_MODE === 'true';
    const delays = isTestMode
      ? [30, 120, 300, 600, 1200] // seconds
      : [10800, 86400, 259200, 604800, 1209600]; // seconds
    const delayIndex = Math.min(newDismissCount - 1, delays.length - 1);
    const nextDelaySeconds = delays[delayIndex];
    const isMaxDismissals = newDismissCount >= delays.length;

    // Track dismissal with comprehensive context
    safeLogEvent('push_permission_maybe_later', {
      context: 'permission_modal',
      user_id: userId,
      dismiss_count: newDismissCount,
      previous_dismiss_count: previousDismissCount,
      next_delay_seconds: nextDelaySeconds,
      is_max_dismissals: isMaxDismissals,
      is_test_mode: isTestMode,
      timestamp: new Date().toISOString(),
    });

    // Location modal operates independently - removed from here
  };

  // Location permission handlers
  const handleLocationAllow = () => {
    setShowLocationModal(false);

    // Dispatch event for game info auto-expand
    window.dispatchEvent(new CustomEvent('locationModalDismissed'));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setPermissionGranted();
          toast.success(LOCATION_MESSAGES.enabled);
          safeLogEvent('location_permission_granted', {
            context: 'onboarding_flow',
          });
        },
        (error) => {
          if (error.code === 1) {
            setPermissionDenied();
            toast.error(LOCATION_MESSAGES.enableInSettings);
            safeLogEvent('location_permission_denied', {
              context: 'browser_prompt',
            });
          }
        }
      );
    }
  };

  const handleLocationDeny = () => {
    setShowLocationModal(false);
    setPermissionDenied();
    safeLogEvent('location_permission_denied', {
      context: 'onboarding_flow',
    });
    // Just dismiss - keep it simple

    // Dispatch event for game info auto-expand
    window.dispatchEvent(new CustomEvent('locationModalDismissed'));
  };

  const handleLocationMaybeLater = () => {
    incrementPromptCount(); // Increment when user clicks Maybe Later
    setMaybeLater(); // Set status to 'maybe-later' so hasResponded() returns true
    setShowLocationModal(false);
    safeLogEvent('location_permission_maybe_later', {
      context: 'onboarding_flow',
    });

    // Dispatch event for game info auto-expand
    window.dispatchEvent(new CustomEvent('locationModalDismissed'));
  };

  return (
    <BodyContainer
      $backgroundColor={
        (configData?.display_one_team === '1' ||
          configData?.display_one_team === 1) &&
        Boolean(configData?.style_main_color)
          ? mainColorRgba(0.6)
          : null
      }
      $backgroundColorEnd={
        (configData?.display_one_team === '1' ||
          configData?.display_one_team === 1) &&
        Boolean(configData?.style_main_color)
          ? mainColorRgba(0)
          : null
      }
      $backgroundColorStart={
        (configData?.display_one_team === '1' ||
          configData?.display_one_team === 1) &&
        Boolean(configData?.style_main_color)
          ? mainColorRgba(0.5)
          : null
      }
      $hasTopNav={isTopNavEnabled}
    >
      <SEO />
      {(() => {
        // Check if we should hide TopNav and Header
        // Detection handles madnesstime.com + mobile device check
        const { isInAppWebView } = detectInAppWebView();

        // console.log('[MainPage] Render check:', {
        //   siteName: configData?.site_name,
        //   isInAppWebView,
        //   willHideTopNav: isInAppWebView,
        // });

        return (
          <>
            {/* Hide TopNav for in app web view on mobile devices */}
            {isTopNavEnabled && !isInAppWebView ? <TopNav /> : null}
            <StyledHeader>
              <Header setEventLeague={setEventLeague} />
            </StyledHeader>
          </>
        );
      })()}
      <MainContainer>
        {/* Only show selection prompts for non-team sites when selection was made but params missing */}
        {(() => {
          const isTeamsSelectSite =
            configData?.display_teams_select === '1' ||
            configData?.display_teams_select === 1;
          // League-scoped sites (display_teams_select on) have a single
          // fixed league with no league selector at all, so params?.league
          // is never set there - requiring it here as well as `team`
          // incorrectly showed "Select League" over an already-selected team.
          const missingSelection = isTeamsSelectSite
            ? !params?.team
            : !params?.league || !params?.team;

          return configData?.display_one_team !== '1' &&
            configData?.display_one_team !== 1 &&
            !isNotSelectionMade &&
            missingSelection ? (
            <ChoiceTitle
              title={
                params?.league === 'Favorites' &&
                (!favoriteTeams || favoriteTeams.length === 0)
                  ? 'Select League'
                  : isTeamsSelectSite && !params?.team
                    ? 'Select Team'
                    : !params?.league
                      ? 'Select League'
                      : 'Select Team'
              }
            />
          ) : null;
        })()}

        {isGamesLoading ? <Loading /> : null}

        {!isGamesLoading && gamesError ? (
          <ErrorMessage title="Unable to display events. Please select a different team or try again later." />
        ) : null}

        {isNotSelectionMade ? (
          <Dashboard
            currentGame={0}
            games={eventsFeedGames}
            setEventLeague={setEventLeague}
          />
        ) : null}

        {shouldDisplayMainDashboard &&
        Boolean(games) &&
        currentGame !== null ? (
          <Dashboard
            currentGame={currentGame}
            games={games}
            setEventLeague={setEventLeague}
          />
        ) : null}

        {shouldDisplayOneLeagueMainDashboard &&
        Boolean(games) &&
        currentGame !== null ? (
          <Dashboard
            currentGame={currentGame}
            games={games}
            setEventLeague={setEventLeague}
          />
        ) : null}

        {(configData?.display_one_team === '1' ||
          configData?.display_one_team === 1) &&
        games?.length ? (
          <DashboardOneTeam
            configuration={configData}
            currentGame={currentGame}
            games={games}
            setEventLeague={setEventLeague}
          />
        ) : null}
      </MainContainer>

      <StyledFooter>
        {isNewsEnabled ? <NewsFeed /> : null}
        <AppStoreLinks />
        <Ads />
      </StyledFooter>

      {/* Onboarding overlay - shows on first visit after 5s delay */}
      {isOnboardingEnabled && !isOnboardingCompleted && mountOnboarding ? (
        <OnboardingOverlay
          onComplete={handleOnboardingComplete}
          show={showOnboarding}
        />
      ) : null}

      {/* Push notification permission modal - shows after onboarding */}
      {isPushNotificationsEnabled && showPushPermissionModal ? (
        <PushNotificationPermissionModal
          dismissCount={
            JSON.parse(
              localStorage.getItem('pushNotificationPermission') || '{}'
            ).dismissCount || 0
          }
          onAllow={handlePushPermissionAllow}
          onMaybeLater={handlePushPermissionMaybeLater}
          show={showPushPermissionModal}
        />
      ) : null}

      {/* Location permission modal - shows after push permission */}
      {isLocationServicesEnabled && showLocationModal ? (
        <LocationPermissionModal
          isThirdPrompt={isThirdPrompt}
          onAllow={handleLocationAllow}
          onDeny={handleLocationDeny}
          onMaybeLater={handleLocationMaybeLater}
          show={showLocationModal}
        />
      ) : null}
    </BodyContainer>
  );
};

export default MainPage;
